/* RESPONSIBLE TEAM: team-reporting */

import { CellType, ChartSchema } from '@/features/report-snapshots/api/schema';
import { ChartType } from '@/features/report-snapshots/types';
import { REPORTING_CONVERSATION_RATINGS } from '@/features/report-snapshots/constants';
import { I18n } from '@lingui/core';

export function getColumnKeys(schema: ChartSchema): string[] {
  if (schema.values.length === 0) return [];
  return Object.keys(schema.values[0]).filter((key) => key !== '0');
}

export function isPercent(schema: ChartSchema): boolean {
  const keys = getColumnKeys(schema);
  return keys.some((key) => isPercentColumn(schema, key));
}

export function isPercentColumn(schema: ChartSchema, key: string): boolean {
  const columnDefinition = schema.columnDefinitions[Number(key)];
  return columnDefinition.unit === 'percent';
}

export function isDuration(schema: ChartSchema): boolean {
  const keys = getColumnKeys(schema);
  return keys.some((key) => isDurationColumn(schema, key));
}

export function isDurationColumn(schema: ChartSchema, key: string): boolean {
  const columnDefinition = schema.columnDefinitions[Number(key)];
  return columnDefinition.unit === 'seconds';
}

export function extractNumericValue(cellValue: CellType): number | null {
  if (typeof cellValue === 'object' && cellValue !== null) {
    if ('numerator' in cellValue) {
      const division = cellValue.numerator / cellValue.denominator;
      return Number.isFinite(division) ? division : null;
    }
    if ('from' in cellValue) {
      return cellValue.from;
    }
  }
  return typeof cellValue === 'number' ? cellValue : null;
}

export function extractRelativeValue(numericValue: number | null, totalSumAbsoluteValues: number) {
  return numericValue ? numericValue / totalSumAbsoluteValues : null;
}

export function translateCellName(cellName: string, i18n: I18n): string {
  if (cellName in REPORTING_CONVERSATION_RATINGS) {
    cellName = i18n._(
      REPORTING_CONVERSATION_RATINGS[cellName as keyof typeof REPORTING_CONVERSATION_RATINGS],
    );
  }
  return cellName;
}

function isSeriesChartsEmpty(chartSchemas: ChartSchema[]): boolean {
  return chartSchemas.every((schema) => {
    return schema.values.every((row) => {
      return Object.keys(row).length === 1;
    });
  });
}

function isDonutChartEmpty(chartSchema: ChartSchema): boolean {
  return chartSchema.values.every((row) => {
    return row['0'] === 'Unknown' || row['0'] === null;
  });
}

function isHeatmapChartEmpty(schema: ChartSchema): boolean {
  return schema.values.every((row) => {
    return Object.keys(row).every((key) => {
      return key === '0' || row[key] === null;
    });
  });
}

function isTabularChartEmpty(chartSchemas: ChartSchema[]): boolean {
  return chartSchemas.every((schema) => {
    return schema.values.every((row) => {
      return row['0'] === 'Unknown' || row['0'] === null;
    });
  });
}

export function isEmptyChart(schemas: ChartSchema[], chartType: ChartType): boolean {
  switch (chartType) {
    case 'pie':
      return isDonutChartEmpty(schemas[0]);
    case 'heatmap':
      return isHeatmapChartEmpty(schemas[0]);
    case 'tabular':
      return isTabularChartEmpty(schemas);
    case 'banner':
      return false; // Banner charts are never empty - they render from description/blocks
    default:
      return isSeriesChartsEmpty(schemas);
  }
}
